import {useParams} from '@/router/router';
import {APIService} from '@/services/api.service';
import {getUserId} from '@/services/auth';
import {buildUserProfile} from '@/store/api/build-problem';
import {
  fetchMyTrips,
  getAllSolutionFilters,
  getAvailableTripThemes,
  getDestName,
  getIntersectionalFilters,
  getMyTrips,
  getSelectedTheme,
  getSessionId,
  getSolution,
  getSolutionContext,
  updateSolutionName,
} from '@/store/state/helpers';
import {saveSolutionToMyTrips} from '@/store/api/save-solution-to-my-trips';
import {trackBtnClick} from '@/store/api/trackBtnClick';
import {getDuration} from '@/store/state/utils';
import {computed, onBeforeUnmount, onMounted, ref} from '@vue/composition-api';
import {VueWithModal} from '../solution-views/summary-filters';
// import {
//   getSurvey,
//   rejectSurvey,
//   saveSurvey,
//   SurveyBackEndAnswer,
//   SurveyQuestionCategory,
// } from '@common/survey/get-survey';
// import SurveyModal from '@common/survey/survey-modal.vue';

export const SESSION_SAVED_TRIP = `amex_saved_trip_session_${process.env.VUE_APP_DATALOG_ENV}`;
export const SAVE_SOLUTION_EVENT = `solution_saved`;

export const openDynamicUrl = (url: string) => {
  const link = document.createElement('a');
  // create a blobURI pointing to our Blob
  link.href = `${url}-${Date.now()}`;
  link.target = '_blank';
  // some browser needs the anchor to be in the doc
  document.body.append(link);
  link.click();
  link.remove();
};

export function useSaveSolutionDialog(root: VueWithModal) {
  const saveDialog = ref(false);
  const isPDFLoading = ref(false);
  const savedSolutionId = ref('');

  const updateSavedTripId = (tripId: string) => {
    savedSolutionId.value = tripId;
  };

  onMounted(() => {
    root?.$on(SAVE_SOLUTION_EVENT, updateSavedTripId);
  });

  onBeforeUnmount(() => {
    root?.$off(SAVE_SOLUTION_EVENT, updateSavedTripId);
  });

  const downloadFile = (blob: Blob, fileName: string) => {
    const link = document.createElement('a');
    // create a blobURI pointing to our Blob
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    // some browser needs the anchor to be in the doc
    document.body.append(link);
    link.click();
    link.remove();
    // in case the Blob uses a lot of memory
    setTimeout(() => URL.revokeObjectURL(link.href), 7000);
  };

  const activeTheme = computed(() => {
    return getSelectedTheme();
  });

  const availableTripOptions = computed(() => {
    return getAvailableTripThemes();
  });

  const selectedTripOption = computed(() => {
    return availableTripOptions.value.find((x) => x.theme === activeTheme.value);
  });

  const onCloseSaveTrip = () => {
    saveDialog.value = false;
  };

  const onSaveTrip = async (name: string) => {
    const userId = getUserId();
    const sessionId = getSessionId();
    const filters = getAllSolutionFilters().filter((x) => x.value);
    const intersectionalFilters = getIntersectionalFilters();

    const userProfile = buildUserProfile(userId, filters, intersectionalFilters);
    await saveSolutionToMyTrips(
      sessionId,
      userId,
      userProfile,
      name || selectedTripOption.value?.context.name || '',
      false,
    );
    await updateSolutionName(name || selectedTripOption.value?.context.name || '');
    const tripId = useParams().value.tripId;
    root.$emit(SAVE_SOLUTION_EVENT, tripId);
    fetchMyTrips();

    // const surveys = await getSurvey(userId);

    saveDialog.value = false;

    // if (surveys.length > 1) {
    //   const survey = surveys[1];
    //   const userId = getUserId();
    //   const surveyId = survey.id;
    //   root.$modal
    //     .show(SurveyModal, {questions: survey.questions})
    //     .then((value: unknown) => {
    //       const formattedValue = value as Record<string, unknown>;
    //       const answers = Object.keys(formattedValue)
    //         .map((questionId) => {
    //           const question = survey.questions.find((x) => x.id === questionId);
    //           switch (question?.category) {
    //             case SurveyQuestionCategory.RATING:
    //               return question.answers[(formattedValue[questionId] as number) - 1];
    //             case SurveyQuestionCategory.OPTION:
    //               return question.answers.find((x) => x.id === formattedValue[questionId]);
    //             case SurveyQuestionCategory.TEXT:
    //               return question.answers.length
    //                 ? {...question.answers[0], description: formattedValue[questionId]}
    //                 : null;
    //             default:
    //               return null;
    //           }
    //         })
    //         .filter(Boolean);
    //       return saveSurvey(userId, surveyId, answers as SurveyBackEndAnswer[]).catch(() => null);
    //     })
    //     .catch(() => rejectSurvey(userId, surveyId, []))
    //     .catch(() => null);
    // }
  };

  const onDownloadPdf = (sessionId?: string, fileName = '') => {
    isPDFLoading.value = true;
    const tripId = getSolution()?.problem_id;
    const tripCoontext = getSolutionContext();
    const destinationName = getDestName();
    const userId = getUserId();
    const path = `/${process.env.VUE_APP_BASE_URL}/pdf/${sessionId || tripId}/${tripCoontext}`.replace(/\/+/g, '/');
    debugger;
    trackBtnClick(userId, `${window.location.origin}${path}`);
    APIService.post(
      'export/itinerary-pdf',
      {
        url: `${process.env.VUE_APP_LOCALHOST_URL || window.location.origin}${path}`.replace(/\/\//g, '/'),
      },
      {responseType: 'blob'},
    )
      .then((data) => {
        downloadFile(
          new Blob([(data as unknown) as BlobPart], {type: 'application/pdf'}),
          fileName || (destinationName ? `${destinationName}.pdf` : 'export.pdf'),
        );
      })
      .catch(() => null)
      .finally(() => {
        saveDialog.value = false;
        isPDFLoading.value = false;
      });
  };

  const tripSavedRelative = () => {
    const date = sessionStorage.getItem('trip-saved-time');
    if (!date) {
      sessionStorage.setItem('trip-saved-time', new Date().valueOf().toString());
    }
    const tripSavedTime = Number(date || new Date().valueOf() - 60 * 1000);
    const duration = getDuration(new Date().valueOf() - tripSavedTime);
    return duration === '0m' ? 'less than 1m' : duration;
  };

  const isSolutionSaved = computed(() => {
    const sessionId = getSessionId();
    const tripId = useParams().value.tripId;
    const savedTrips = getMyTrips().filter((x) => !x.autoSaved);
    const trip = savedTrips.find((x) => x.id === sessionId || x.id === tripId || x.tripId === tripId);
    return !!trip;
  });

  const tripId = computed(() => {
    const sessionId = getSessionId();
    const tripId = useParams().value.tripId;
    const savedTrips = getMyTrips().filter((x) => !x.autoSaved);
    const trip = savedTrips.find((x) => x.id === sessionId || x.id === tripId || x.tripId === tripId);
    return trip?.tripId || savedSolutionId.value;
  });

  // fetchMyTrips();

  return {
    saveDialog,
    onCloseSaveTrip,
    onSaveTrip,
    onDownloadPdf,
    tripSavedRelative,
    isSolutionSaved,
    isPDFLoading,
    tripId,
  };
}
